import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Portal from '@material-ui/core/Portal';
import EditIntegrationDrawer from '../../../components/EditIntegrationDrawer';
import IntegrationPreview from './IntegrationPreview';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { apps as appsApi } from '@afosto/apps/api/Apps';
import { integrations as integrationsApi } from '@afosto/apps/api/Integrations';
import { integrationFormatter } from './formatters';
import translations from './translations';

const IntegrationsList = ({ previewRef }) => {
  const confirm = useConfirm();
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [app, setApp] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [isFetchingIntegration, setIsFetchingIntegration] = useState(true);
  const [showEditIntegration, setShowEditIntegration] = useState(false);

  const fetchIntegrations = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (source, requestOptions) =>
        integrationsApi.listIntegrations({ isDeleted: 0 }, requestOptions),
      {
        transformData: data => data,
      },
    );

  const dataSource = useDataGrid(fetchIntegrations, {
    modules: ['activeItem', 'dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'name',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.name',
            defaultMessage: 'Name',
            description: 'The column name for column name',
          }),
          type: 'integration',
        },
        {
          key: 'app_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.app',
            defaultMessage: 'App',
            description: 'The column name for column app',
          }),
        },
        {
          key: 'is_installed',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.isInstalled',
            defaultMessage: 'Installed',
            description: 'The column name for column is installed',
          }),
          type: 'boolean',
        },
        {
          key: 'active_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.activeAt',
            defaultMessage: 'Active at',
            description: 'The column name for column active at',
          }),
          type: 'datetime',
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
        {
          key: 'deleted_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.integrations.deletedAt',
            defaultMessage: 'Deleted at',
            description: 'The column name for column deleted at',
          }),
          type: 'datetime',
        },
      ],
    },
  });
  const activeItem = dataSource.activeItem?.activeItem;

  useEffect(() => {
    let isMounted = true;

    const fetchIntegration = async id => {
      setIsFetchingIntegration(true);

      try {
        const response = await integrationsApi.getIntegration(id);
        const { data = {} } = response;

        if (data?.app_code) {
          const appResponse = await appsApi.getApp(data.app_code);
          const { data: appData = null } = appResponse ?? {};

          if (isMounted) {
            setApp(appData);
          }
        }

        if (isMounted) {
          setIntegration(data);
        }
      } catch (err) {
        if (isMounted) {
          setApp(null);
          setIntegration(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingIntegration(false);
        }
      }
    };

    if (activeItem) {
      fetchIntegration(activeItem);
    } else {
      setApp(null);
      setIntegration(null);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem]);

  const handleSaveIntegration = updatedIntegration => {
    setIntegration(updatedIntegration);
    setShowEditIntegration(false);
    dataSource.reloadDataSource();
  };

  const handleDeleteIntegration = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteIntegration.title),
        description: intl.formatMessage(translations.confirmDeleteIntegration.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteIntegration.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteIntegration.buttons.delete),
            response: 'deleteIntegration',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'deleteIntegration') {
        return;
      }

      await integrationsApi.disable(integration.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.integrationDeleted), {
        variant: 'success',
      });

      dataSource.reloadDataSource();
      dataSource.activeItem.setActiveItem(null);
      setIntegration(null);
      setApp(null);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.integrationNotDeleted), {
        variant: 'error',
      });
    }
  };

  const previewMenu = [
    ...(integration
      ? [
          {
            key: 'integration_view',
            translationKey: 'actions.view',
            label: 'View',
            icon: 'eye',
            shortcut: true,
            onClick: () => history.push(`/integrations/${integration.id}`),
          },
          ...(app
            ? [
                {
                  key: 'integration_edit',
                  translationKey: 'actions.edit',
                  label: 'Edit',
                  icon: 'pencil',
                  shortcut: true,
                  onClick: () => setShowEditIntegration(true),
                },
              ]
            : []),
          {
            key: 'integration_delete',
            translationKey: 'actions.delete',
            label: 'Delete',
            icon: 'trash-alt',
            color: 'secondary',
            onClick: handleDeleteIntegration,
          },
        ]
      : []),
  ];

  return (
    <>
      <DataGrid dataSource={dataSource} formatters={{ integration: integrationFormatter }} />
      <Portal container={previewRef.current}>
        <IntegrationPreview
          isOpen={!!activeItem}
          app={app}
          integration={integration}
          isLoading={isFetchingIntegration}
          menu={previewMenu}
          onClose={() => dataSource.activeItem.setActiveItem(null)}
        />
      </Portal>
      <EditIntegrationDrawer
        app={app}
        integration={integration}
        isOpen={showEditIntegration}
        onClose={() => setShowEditIntegration(false)}
        onSave={handleSaveIntegration}
      />
    </>
  );
};

IntegrationsList.propTypes = {
  previewRef: PropTypes.object,
};

IntegrationsList.defaultProps = {
  previewRef: undefined,
};

export default IntegrationsList;
